// What can be counted. A measure is a number the analytics layer knows how to
// compute; how it is computed in *this* schema is the repository's business
// (`MEASURE_SQL` in `src/modules/analytics/analytics.repository.ts`), and this
// file must never hint at a table or a column.

export const MEASURE_IDS = ["units", "lines"] as const;

export type MeasureId = (typeof MEASURE_IDS)[number];

// Deliberately a union of one. Both v1 measures are whole counts, and the
// frontend formats on an exhaustive switch — so the day a decimal or money
// measure lands, widening this union turns every unhandled formatter into a
// compile error. Pre-declaring "currency" would instead let it fall silently
// through a default branch.
//
// That guarantee has a precondition the formatter must honour: switch on this
// with a declared non-optional return type and NO `default` branch (or an
// `assertNever` in one). A `default: return String(n)` throws the whole argument
// away — the new variant compiles and renders wrongly.
export type MeasureFormat = "integer";

// What a figure of this measure is counted *in*, which is what decides whether a
// sum of it across items is one comparable number.
//
//   - `item_unit`: counted in each item's own unit. A clinic issues Panadol in
//     tablets and a syrup in millilitres, so a cross-item sum adds up things that
//     are not the same thing — indicative, not exact, and the UI has to say so.
//   - `count`: counted in occurrences, which mean the same thing whatever they
//     are of, so a sum is exact.
//
// Same discipline as `MeasureFormat`: no `"currency"` until a money measure
// exists, so that the day one lands every consumer that branches on this stops
// compiling instead of silently taking the branch meant for something else.
export type MeasureDenomination = "item_unit" | "count";

export type MeasureMeta = {
  readonly id: MeasureId;
  /** i18n key. The contract never carries display text — only keys. */
  readonly labelKey: string;
  readonly format: MeasureFormat;
  /**
   * What the number counts in. The frontend qualifies figures that a cross-item
   * sum makes indicative, and reads this to know which ones those are rather
   * than testing the measure's id.
   */
  readonly denomination: MeasureDenomination;
};

// `lines` counts issue rows; the UI labels it "Issues" because that is what a
// dispenser calls it. The id stays `lines` so it can't be confused with the
// `stock_issues` fact itself.
export const MEASURES = {
  units: {
    id: "units",
    labelKey: "analytics.measure.units",
    format: "integer",
    denomination: "item_unit",
  },
  lines: {
    id: "lines",
    labelKey: "analytics.measure.lines",
    format: "integer",
    denomination: "count",
  },
} as const satisfies Record<MeasureId, MeasureMeta>;
